// Admin-only API for the 3-minute estimate builder.
// Every endpoint below is either `estimate/admin/*` or `estimate/analytics/*`,
// so all of them go through `authAxios` (bearer token + 401 re-login flow).
// The public visitor-facing calls (questionnaire/**, sessions/**) live in
// `src/auto-estimate/services/estimateApi.js` and must stay unauthenticated.
import { authAxios } from './apiClient';

const API_URL = '/api/estimate';

// --- Versions ---

export const getVersions = async () => {
    try {
        const response = await authAxios.get(`${API_URL}/admin/versions`);
        return response.data;
    } catch (error) {
        console.error('API Error: getVersions', error);
        throw error;
    }
};

export const getVersion = async (id) => {
    const response = await authAxios.get(`${API_URL}/admin/versions/${id}`);
    return response.data;
};

export const createVersion = async (data) => {
    const response = await authAxios.post(`${API_URL}/admin/versions`, data);
    return response.data;
};

export const updateVersion = async (id, data) => {
    const response = await authAxios.put(`${API_URL}/admin/versions/${id}`, data);
    return response.data;
};

export const deleteVersion = async (id) => {
    const response = await authAxios.delete(`${API_URL}/admin/versions/${id}`);
    return response.data;
};

export const cloneVersion = async (id) => {
    const response = await authAxios.post(`${API_URL}/admin/versions/${id}/clone`);
    return response.data;
};

// --- Questions ---

export const getQuestions = async (versionId) => {
    const response = await authAxios.get(`${API_URL}/admin/versions/${versionId}/questions`);
    return response.data;
};

export const createQuestion = async (versionId, data) => {
    const response = await authAxios.post(`${API_URL}/admin/versions/${versionId}/questions`, data);
    return response.data;
};

export const updateQuestion = async (id, data) => {
    const response = await authAxios.put(`${API_URL}/admin/questions/${id}`, data);
    return response.data;
};

export const deleteQuestion = async (id) => {
    const response = await authAxios.delete(`${API_URL}/admin/questions/${id}`);
    return response.data;
};

// --- Options ---

export const createOption = async (questionId, data) => {
    const response = await authAxios.post(`${API_URL}/admin/questions/${questionId}/options`, data);
    return response.data;
};

export const updateOption = async (id, data) => {
    const response = await authAxios.put(`${API_URL}/admin/options/${id}`, data);
    return response.data;
};

export const updateOptionLabelsBulk = async (questionId, updates) => {
    const response = await authAxios.put(
        `${API_URL}/admin/questions/${questionId}/option-labels`,
        { updates },
    );
    return response.data;
};

export const deleteOption = async (id) => {
    const response = await authAxios.delete(`${API_URL}/admin/options/${id}`);
    return response.data;
};

// --- Option Pricing Effects ---

export const getOptionEffects = async (optionId) => {
    const response = await authAxios.get(`${API_URL}/admin/options/${optionId}/effects`);
    return response.data;
};

export const createOptionEffect = async (optionId, data) => {
    const response = await authAxios.post(`${API_URL}/admin/options/${optionId}/effects`, data);
    return response.data;
};

export const updateOptionEffect = async (effectId, data) => {
    const response = await authAxios.put(`${API_URL}/admin/effects/${effectId}`, data);
    return response.data;
};

export const deleteOptionEffect = async (effectId) => {
    const response = await authAxios.delete(`${API_URL}/admin/effects/${effectId}`);
    return response.data;
};

// --- Components ---

export const getComponents = async () => {
    const response = await authAxios.get(`${API_URL}/admin/components`);
    return response.data;
};

export const createComponent = async (versionId, data) => {
    const response = await authAxios.post(`${API_URL}/admin/components`, data);
    return response.data;
};

export const updateComponent = async (id, data) => {
    const response = await authAxios.put(`${API_URL}/admin/components/${id}`, data);
    return response.data;
};

export const deleteComponent = async (id) => {
    const response = await authAxios.delete(`${API_URL}/admin/components/${id}`);
    return response.data;
};

export const duplicateQuestion = async (id) => {
    const response = await authAxios.post(`${API_URL}/admin/questions/${id}/duplicate`);
    return response.data;
};

// --- Analytics ---

export const getDashboardStats = async (period = '1w') => {
    const response = await authAxios.get(`${API_URL}/analytics/dashboard?period=${period}`);
    return response.data;
};

export const getDropOffStats = async () => {
    const response = await authAxios.get(`${API_URL}/analytics/drop-off`);
    return response.data;
};

export const getCategoryStats = async () => {
    const response = await authAxios.get(`${API_URL}/analytics/categories`);
    return response.data;
};

export const getRecentEstimates = async () => {
    const response = await authAxios.get(`${API_URL}/analytics/recent`);
    return response.data;
};
